import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import fr from './locales/fr';
import en from './locales/en';

const resources = {
  fr,
  en,
};
const STORAGE_KEY = 'siteLanguage';
const missingKeys = new Set();

function getNested(obj, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str;
  return Object.entries(vars).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`{{${key}}}`, 'g'), String(value)),
    str,
  );
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ar') return 'fr';
    return saved && resources[saved] ? saved : 'fr';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = 'ltr';
    document.body.classList.remove('rtl-layout');
  }, [language]);

  const t = useMemo(() => {
    const dict = resources[language] || resources.fr;
    const fallback = resources.fr;
    const translate = (key, vars) => {
      const count = vars?.count;
      let resolvedKey = key;
      if (count !== undefined && count !== 1 && getNested(dict, `${key}_plural`)) {
        resolvedKey = `${key}_plural`;
      }
      const currentValue = getNested(dict, resolvedKey);
      const fallbackValue = getNested(fallback, resolvedKey);
      const value = currentValue ?? fallbackValue ?? key;
      if (currentValue === undefined && !missingKeys.has(resolvedKey)) {
        missingKeys.add(resolvedKey);
        // eslint-disable-next-line no-console
        console.warn(
          `Missing translation key: "${resolvedKey}" (language: ${language})${fallbackValue !== undefined ? ' - fallback used' : ''}`,
        );
      }
      return interpolate(value, vars);
    };
    // Lookup silencieux : permet de tester l'existence d'une clé (langue courante
    // ou fallback fr) sans déclencher l'avertissement « Missing translation key ».
    translate.has = (key) =>
      getNested(dict, key) !== undefined || getNested(fallback, key) !== undefined;
    return translate;
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      isRtl: false,
    }),
    [language, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
}

export const STRIPE_LOCALES = { fr: 'fr', en: 'en' };
